import { useEffect } from "react";
import isEmail from "validator/lib/isEmail";
import { safeURIDecode, decryptData } from "../utils";

// Name validator (letters, diacritics, hyphen, apostrophe, space allowed)
const isValidName = (name) => /^[\p{L}][\p{L}'\- ]*$/u.test(name.trim());

// Centralized rules
const PARAM_RULES = {
  email: {
    validate: isEmail,
    decode: safeURIDecode,
  },
  fname: {
    validate: isValidName,
    decode: safeURIDecode,
  },
  lname: {
    validate: isValidName,
    decode: safeURIDecode,
  },
  // Add more params and rules here as needed
};

/**
 * Redirects if any required param is missing or fails its validator.
 * If "data" param is present, tries to decrypt and validate its contents.
 * If not, falls back to normal param validation.
 * @param {object} options
 * @param {URLSearchParams} options.searchParams
 * @param {Array} options.required
 * @param {string} [options.redirectUrl="/"]
 * @param {string} [options.decryptPassword] - Password for decrypting "data" param
 */
export function useParamValidationRedirect({
  searchParams,
  required = ["email"],
  redirectUrl = "/",
  decryptPassword = process.env.REACT_APP_DECRYPT_PASSWORD,
  skipValidation = false,
}) {
  useEffect(() => {

    if (skipValidation) return; // skip all validation

    const encrypted = searchParams.get("data");
    if (encrypted) {
      (async () => {
        try {
          const decrypted = await decryptData(encrypted, decryptPassword);
          if (!decrypted) throw new Error("Failed to decrypt data param");
          const parsed = JSON.parse(decrypted);

          // Validate required fields in decrypted data (dynamic, not hardcoded)
          for (const req of required) {
            let keys, ruleKey;
            if (typeof req === "string") {
              keys = [req];
              ruleKey = req;
            } else if (
              typeof req === "object" &&
              Array.isArray(req.keys) &&
              req.rule
            ) {
              keys = req.keys;
              ruleKey = req.rule;
            } else {
              continue; // skip invalid config
            }

            const rule = PARAM_RULES[ruleKey];
            let foundValid = false;

            for (const key of keys) {
              const value = parsed[key];
              const decode = rule?.decode || ((v) => v);
              if (value && rule?.validate(decode(value))) {
                foundValid = true;
                break;
              }
            }

            if (!foundValid) {
              throw new Error("Invalid or missing fields in decrypted data");
            }
          }
          // If all checks pass, do nothing (user stays)
        } catch (e) {
          window.location.href = redirectUrl;
        }
      })();
      return; // Don't run the rest of the checks if "data" param is present
    } else {
      //! Note: If you want to allow "data" param to be optional and also check for other params for email, remove this else block and let the fallback code below handle it
      window.location.href = redirectUrl; // Redirect if no "data" param
      return;
    }

    //! Note: Currently we are only checking for email in data param so this code is not needed. But it can be used if we want to check for other email params in the URL later
    // Fallback: check other params for the data you need
    for (const req of required) {
      let keys, ruleKey;
      if (typeof req === "string") {
        keys = [req];
        ruleKey = req;
      } else if (
        typeof req === "object" &&
        Array.isArray(req.keys) &&
        req.rule
      ) {
        keys = req.keys;
        ruleKey = req.rule;
      } else {
        continue; // skip invalid config
      }

      const rule = PARAM_RULES[ruleKey];
      let foundValid = false;

      for (const key of keys) {
        const value = searchParams.get(key);
        const decode = rule?.decode || ((v) => v);
        if (value && rule?.validate(decode(value))) {
          foundValid = true;
          break;
        }
      }

      if (!foundValid) {
        window.location.href = redirectUrl;
        break;
      }
    }
  }, [searchParams, required, redirectUrl, decryptPassword]);
}
